<script setup>
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { useRoadmapsStore } from '@/stores/roadmaps';
import { ui, openSettings } from '@/composables/useUi';
import { confirm, toast } from '@/composables/useDialogs';
import { totalSlots, snapUnit, blocksToSlots, slotsToBlocks, SLOTS_PER_SPRINT, STATUSES, DEFAULT_ALLOCATION, projectExtent, safeUrl, uid } from '@/lib/schema';
import { hasDates, formatDateRange, rangeDates, formatDay, sprintStartDate } from '@/lib/dates';
import { groupNames } from '@/lib/groups';
import { phaseVars } from '@/lib/palette';
import Modal from '@/components/ui/Modal.vue';
import AppButton from '@/components/ui/AppButton.vue';
import Icon from '@/components/ui/Icon.vue';

const ERASE = '__erase__';

const store = useRoadmapsStore();

const roadmap = computed(() => (ui.project.roadmapId ? store.byId(ui.project.roadmapId) : null));
const project = computed(() => roadmap.value?.projects.find((p) => p.id === ui.project.projectId) || null);
const isCreate = computed(() => !ui.project.projectId);

const name = ref('');
const description = ref('');
const memberIds = ref([]);
const group = ref('');
const status = ref('');
const allocation = ref({});
const dependsOn = ref([]);
const links = ref([]);
const linkLabel = ref('');
const linkUrl = ref('');
const slots = ref([]);
const brush = ref(null);
let painting = false;
let strokeValue = null;

const total = computed(() => (roadmap.value ? totalSlots(roadmap.value) : 0));
const unit = computed(() => (roadmap.value ? snapUnit(roadmap.value) : SLOTS_PER_SPRINT));
const phaseById = computed(() => new Map((roadmap.value?.phases || []).map((p) => [p.id, p])));

watch(
  () => ui.project.open,
  (open) => {
    if (!open || !roadmap.value) return;
    const p = project.value;
    name.value = p?.name || '';
    description.value = p?.description || '';
    memberIds.value = p ? [...p.memberIds] : [];
    group.value = p?.group || '';
    status.value = p?.status || '';
    allocation.value = { ...(p?.allocation || {}) };
    dependsOn.value = [...(p?.dependsOn || [])];
    links.value = (p?.links || []).map((l) => ({ ...l }));
    linkLabel.value = '';
    linkUrl.value = '';
    slots.value = blocksToSlots(p?.blocks || [], total.value);
    brush.value = roadmap.value.phases.length ? store.defaultPhaseId(roadmap.value.id) : '';
  }
);

const ALLOCATION_OPTIONS = [10, 20, 25, 30, 40, 50, 60, 70, 75, 80, 90, 100];
const statusOptions = [{ value: '', label: 'Not set' }, ...STATUSES];
const groups = computed(() => groupNames(roadmap.value?.projects || []));
const dated = computed(() => (roadmap.value ? hasDates(roadmap.value) : false));
const selectedMembers = computed(() => (roadmap.value?.members || []).filter((m) => memberIds.value.includes(m.id)));
const otherProjects = computed(() => (roadmap.value?.projects || []).filter((p) => p.id !== project.value?.id));

function allocationValue(id) {
  return allocation.value[id] ?? '';
}

function setAllocation(id, value) {
  const next = { ...allocation.value };
  if (value === '' || value === null) delete next[id];
  else next[id] = Number(value);
  allocation.value = next;
}

function toggleDependency(id) {
  dependsOn.value = dependsOn.value.includes(id) ? dependsOn.value.filter((x) => x !== id) : [...dependsOn.value, id];
}

const plannedExtent = computed(() => projectExtent({ blocks: slotsToBlocks(slots.value) }));

const planSummary = computed(() => {
  const ext = plannedExtent.value;
  if (!ext || !dated.value) return '';
  return formatDateRange(rangeDates(roadmap.value.settings, ext.start, ext.span));
});

const dependencyWarnings = computed(() => {
  const mine = plannedExtent.value;
  if (!mine) return [];
  return dependsOn.value
    .map((id) => otherProjects.value.find((p) => p.id === id))
    .filter(Boolean)
    .filter((dep) => {
      const theirs = projectExtent(dep);
      return theirs && mine.start < theirs.end;
    })
    .map((dep) => dep.name);
});

const linkValid = computed(() => Boolean(safeUrl(linkUrl.value)));

function addLink() {
  const url = safeUrl(linkUrl.value);
  if (!url) return;
  links.value = [...links.value, { id: uid(), label: linkLabel.value.trim(), url }];
  linkLabel.value = '';
  linkUrl.value = '';
}

function removeLink(id) {
  links.value = links.value.filter((l) => l.id !== id);
}

function sprintTitle(n) {
  return dated.value ? `Sprint ${n}, from ${formatDay(sprintStartDate(roadmap.value.settings, n))}` : `Sprint ${n}`;
}

function toggleMember(id) {
  const i = memberIds.value.indexOf(id);
  if (i === -1) memberIds.value.push(id);
  else memberIds.value.splice(i, 1);
}

function cellStyle(value) {
  if (value === null || value === undefined) return null;
  const phase = value ? phaseById.value.get(value) : null;
  return phaseVars(phase ? phase.color : null);
}

function runEdge(i) {
  const v = slots.value[i];
  if (v === null) return {};
  return {
    'run-start': i === 0 || slots.value[i - 1] !== v,
    'run-end': i === slots.value.length - 1 || slots.value[i + 1] !== v
  };
}

function paint(i, value) {
  const u = unit.value;
  const from = Math.floor(i / u) * u;
  for (let s = from; s < from + u && s < slots.value.length; s++) slots.value[s] = value;
}

function onCellDown(e, i) {
  if (e.button !== 0) return;
  e.preventDefault();
  const target = brush.value === ERASE ? null : brush.value;
  strokeValue = slots.value[i] === target ? null : target;
  painting = true;
  paint(i, strokeValue);
  window.addEventListener('pointerup', endStroke, { once: true });
}

function onCellEnter(i) {
  if (painting) paint(i, strokeValue);
}

function endStroke() {
  painting = false;
}

function clearTimeline() {
  slots.value = slots.value.map(() => null);
}

function close() {
  ui.project.open = false;
}

function save() {
  if (!name.value.trim() || !roadmap.value) return;
  if (linkValid.value) addLink();
  const data = {
    name: name.value.trim(),
    description: description.value.trim(),
    memberIds: memberIds.value,
    blocks: slotsToBlocks(slots.value),
    group: group.value.trim(),
    status: status.value,
    allocation: Object.fromEntries(Object.entries(allocation.value).filter(([id]) => memberIds.value.includes(id))),
    dependsOn: dependsOn.value.filter((id) => otherProjects.value.some((p) => p.id === id)),
    links: links.value.filter((l) => safeUrl(l.url)).map((l) => ({ ...l, url: safeUrl(l.url) }))
  };
  if (isCreate.value) {
    store.addProject(roadmap.value.id, data);
    toast(`${data.name} added`);
  } else {
    store.updateProject(roadmap.value.id, project.value.id, data);
  }
  close();
}

function duplicate() {
  store.duplicateProject(roadmap.value.id, project.value.id);
  close();
  toast('Project duplicated');
}

async function remove() {
  const ok = await confirm({
    title: `Delete ${project.value.name}?`,
    message: 'The project and all of its blocks will be removed from this roadmap.',
    confirmLabel: 'Delete project',
    danger: true
  });
  if (!ok) return;
  store.removeProject(roadmap.value.id, project.value.id);
  close();
}

function manageTeam() {
  const id = roadmap.value.id;
  close();
  openSettings(id, 'team');
}

function managePhases() {
  const id = roadmap.value.id;
  close();
  openSettings(id, 'phases');
}

onBeforeUnmount(() => window.removeEventListener('pointerup', endStroke));
</script>

<template>
  <Modal :open="ui.project.open && !!roadmap" :title="isCreate ? 'Add project' : 'Edit project'" :width="720" @close="close">
    <form v-if="roadmap" @submit.prevent="save">
      <label class="field">
        <span class="field-label">Name</span>
        <input v-model="name" class="input" type="text" placeholder="What is being delivered" autofocus />
      </label>

      <div class="field-row">
        <label class="field field--grow">
          <span class="field-label">Group</span>
          <input v-model="group" class="input" type="text" list="mm-groups" placeholder="Optional team, theme or initiative" />
          <datalist id="mm-groups">
            <option v-for="g in groups" :key="g" :value="g" />
          </datalist>
        </label>
        <div class="field">
          <span class="field-label">Status</span>
          <div class="seg-pick" role="radiogroup" aria-label="Status">
            <button
              v-for="opt in statusOptions"
              :key="opt.value"
              type="button"
              role="radio"
              class="seg-opt"
              :class="{ on: status === opt.value }"
              :aria-checked="status === opt.value"
              :title="opt.hint || 'No status shown'"
              @click="status = opt.value"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>
      </div>

      <label class="field">
        <span class="field-label">Description</span>
        <textarea v-model="description" class="textarea" rows="2" placeholder="Optional context or goals" />
      </label>

      <div class="field">
        <span class="field-label">Team</span>
        <div v-if="roadmap.members.length" class="chips">
          <button
            v-for="m in roadmap.members"
            :key="m.id"
            type="button"
            class="chip"
            :class="{ on: memberIds.includes(m.id) }"
            :aria-pressed="memberIds.includes(m.id)"
            @click="toggleMember(m.id)"
          >
            <Icon v-if="memberIds.includes(m.id)" name="check" :size="13" />
            {{ m.name }}
          </button>
          <button type="button" class="chip chip--link" @click="manageTeam">Manage team</button>
        </div>
        <p v-else class="field-hint">
          Nobody is on this roadmap yet.
          <button type="button" class="inline-link" @click="manageTeam">Add team members</button>
        </p>
        <div v-if="selectedMembers.length" class="alloc">
          <span class="field-hint">Time on this project</span>
          <label v-for="m in selectedMembers" :key="m.id" class="alloc-row">
            <span class="alloc-name">{{ m.name }}</span>
            <select :value="allocationValue(m.id)" class="select alloc-select" :aria-label="`Time ${m.name} spends on this project`" @change="setAllocation(m.id, $event.target.value)">
              <option value="">{{ DEFAULT_ALLOCATION }}% (default)</option>
              <option v-for="v in ALLOCATION_OPTIONS" :key="v" :value="v">{{ v }}%</option>
            </select>
          </label>
        </div>
      </div>

      <div class="field">
        <div class="timeline-head">
          <span class="field-label">Timeline</span>
          <button v-if="slots.some((s) => s !== null)" type="button" class="inline-link" @click="clearTimeline">Clear</button>
        </div>
        <div class="brushes" role="radiogroup" aria-label="Paint with">
          <button
            v-for="phase in roadmap.phases"
            :key="phase.id"
            type="button"
            class="brush"
            :class="{ on: brush === phase.id }"
            role="radio"
            :aria-checked="brush === phase.id"
            :style="phaseVars(phase.color)"
            @click="brush = phase.id"
          >
            <i />{{ phase.name }}
          </button>
          <button
            v-if="!roadmap.phases.length"
            type="button"
            class="brush"
            :class="{ on: brush === '' }"
            role="radio"
            :aria-checked="brush === ''"
            :style="phaseVars(null)"
            @click="brush = ''"
          >
            <i />No phase
          </button>
          <button type="button" class="brush brush--erase" :class="{ on: brush === ERASE }" role="radio" :aria-checked="brush === ERASE" @click="brush = ERASE">
            <Icon name="eraser" :size="13" />Erase
          </button>
          <button type="button" class="inline-link brushes-edit" @click="managePhases">Edit phases</button>
        </div>

        <div class="painter-scroll">
          <div class="painter" :class="{ half: unit === 1 }" :style="{ '--slots': total }">
            <span v-for="n in total / 2" :key="`h${n}`" class="sprint-label" :title="sprintTitle(n)" :style="{ gridColumn: `${(n - 1) * 2 + 1} / span 2` }">S{{ n }}</span>
            <button
              v-for="(value, i) in slots"
              :key="i"
              type="button"
              class="cell"
              :class="[{ filled: value !== null, odd: i % 2 === 1 }, runEdge(i)]"
              :style="cellStyle(value)"
              :aria-label="`Sprint ${Math.floor(i / 2) + 1}${unit === 1 ? (i % 2 ? ' second half' : ' first half') : ''}`"
              @pointerdown="onCellDown($event, i)"
              @pointerenter="onCellEnter(i)"
            />
          </div>
        </div>
        <p class="field-hint">
          <template v-if="planSummary"><strong class="plan-dates">{{ planSummary }}.</strong> </template>
          Pick a phase, then click or drag across sprints to paint. Paint over a phase to swap it, or click it again to clear.
        </p>
      </div>

      <div v-if="otherProjects.length" class="field">
        <span class="field-label">Depends on</span>
        <div class="chips">
          <button
            v-for="p in otherProjects"
            :key="p.id"
            type="button"
            class="chip"
            :class="{ on: dependsOn.includes(p.id) }"
            :aria-pressed="dependsOn.includes(p.id)"
            @click="toggleDependency(p.id)"
          >
            <Icon v-if="dependsOn.includes(p.id)" name="check" :size="13" />
            {{ p.name }}
          </button>
        </div>
        <p v-if="dependencyWarnings.length" class="dep-warn">
          <Icon name="alert" :size="14" />
          This starts before {{ dependencyWarnings.join(' and ') }} {{ dependencyWarnings.length === 1 ? 'finishes' : 'finish' }}.
        </p>
      </div>

      <div class="field">
        <span class="field-label">Links</span>
        <ul v-if="links.length" class="links">
          <li v-for="l in links" :key="l.id" class="link-row">
            <Icon name="link" :size="13" />
            <input v-model="l.label" class="link-label" type="text" :aria-label="`Label for ${l.url}`" placeholder="Label" />
            <a :href="l.url" target="_blank" rel="noopener noreferrer" class="link-url">{{ l.url }}</a>
            <button type="button" class="link-remove" :aria-label="`Remove ${l.label || l.url}`" @click="removeLink(l.id)">
              <Icon name="close" :size="13" />
            </button>
          </li>
        </ul>
        <div class="link-add">
          <input v-model="linkLabel" class="input link-add-label" type="text" placeholder="Label, for example Spec" @keydown.enter.prevent="addLink" />
          <input v-model="linkUrl" class="input" type="url" placeholder="https://" @keydown.enter.prevent="addLink" />
          <AppButton icon="plus" :disabled="!linkValid" @click="addLink">Add</AppButton>
        </div>
      </div>
      <button type="submit" class="sr-only" tabindex="-1">Save</button>
    </form>

    <template #footer>
      <template v-if="!isCreate && project">
        <AppButton variant="danger-ghost" icon="trash" size="sm" @click="remove">Delete</AppButton>
        <AppButton variant="ghost" icon="copy" size="sm" @click="duplicate">Duplicate</AppButton>
      </template>
      <span class="spacer" />
      <AppButton variant="ghost" @click="close">Cancel</AppButton>
      <AppButton variant="primary" :disabled="!name.trim()" @click="save">{{ isCreate ? 'Add project' : 'Save changes' }}</AppButton>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid var(--line-strong);
  border-radius: 999px;
  background: var(--surface);
  color: var(--text-2);
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s, color 0.15s;

  &:hover {
    border-color: var(--text-3);
    color: var(--text);
  }

  &.on {
    border-color: var(--iris);
    background: var(--iris-soft);
    color: var(--iris-strong);
    font-weight: 500;
  }

  &--link {
    border-style: dashed;
    color: var(--text-3);
  }
}

.inline-link {
  padding: 0;
  border: 0;
  background: none;
  color: var(--iris-strong);
  font-size: 12px;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.timeline-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.brushes {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.brushes-edit {
  margin-left: 6px;
}

.brush {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid var(--line);
  border-radius: 7px;
  background: var(--surface);
  color: var(--text-2);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;

  i {
    width: 9px;
    height: 9px;
    border-radius: 3px;
    background: var(--phase);
  }

  &:hover {
    border-color: var(--line-strong);
  }

  &.on {
    border-color: var(--phase, var(--text));
    background: color-mix(in srgb, var(--phase, #8a8ba0) 12%, var(--mix-base));
    color: var(--phase-text, var(--text));
  }

  &--erase.on {
    border-color: var(--text-2);
    background: var(--bg);
    color: var(--text);
  }
}

.painter-scroll {
  overflow-x: auto;
  margin-top: 4px;
  padding-bottom: 2px;
}

.painter {
  display: grid;
  grid-template-columns: repeat(var(--slots), minmax(11px, 1fr));
  grid-template-rows: auto 40px;
  row-gap: 6px;
  user-select: none;
  touch-action: none;
}

.sprint-label {
  grid-row: 1;
  font-size: 11px;
  font-weight: 500;
  color: var(--text-3);
  text-align: center;
}

.cell {
  grid-row: 2;
  padding: 0;
  margin: 0 0 0 3px;
  border: 1px solid var(--line);
  border-right: 0;
  border-radius: 7px 0 0 7px;
  background: var(--surface-2);
  cursor: pointer;

  &.odd {
    margin: 0 3px 0 0;
    border-left: 0;
    border-right: 1px solid var(--line);
    border-radius: 0 7px 7px 0;
  }

  .half &.odd {
    border-left: 1px dashed var(--line-strong);
  }

  &:hover {
    background: var(--bg);
  }

  &.filled {
    background: color-mix(in srgb, var(--phase) 22%, var(--mix-base));
    border-color: color-mix(in srgb, var(--phase) 45%, var(--mix-base));

    &:hover {
      background: color-mix(in srgb, var(--phase) 32%, var(--mix-base));
    }
  }

  &.filled.run-start:not(.odd) {
    box-shadow: inset 3px 0 0 var(--phase);
  }
}

.spacer {
  flex: 1;
}

.field-row {
  display: flex;
  gap: 16px;
  margin-top: 18px;

  .field + .field {
    margin-top: 0;
  }
}

.field--grow {
  flex: 1;
}

.field-row + .field {
  margin-top: 18px;
}

.seg-pick {
  display: inline-flex;
  padding: 3px;
  gap: 2px;
  border-radius: 9px;
  background: var(--track-bg);
}

.seg-opt {
  height: 30px;
  padding: 0 10px;
  border: 0;
  border-radius: 7px;
  background: none;
  color: var(--text-2);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: var(--text);
  }

  &.on {
    background: var(--surface);
    color: var(--text);
    box-shadow: var(--shadow-1);
  }
}

.alloc {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 14px;
  margin-top: 8px;
  padding: 10px 12px;
  border-radius: var(--radius-s);
  background: var(--bg);
}

.alloc-row {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
}

.alloc-select {
  width: auto;
  padding: 3px 6px;
  font-size: 12px;
}

.plan-dates {
  color: var(--text-2);
  font-weight: 600;
}

.dep-warn {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  padding: 8px 10px;
  border-radius: var(--radius-s);
  background: var(--warn-bg);
  color: var(--warn-text);
  font-size: 12px;
}

.links {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid var(--line);
  border-radius: var(--radius-s);
}

.link-row {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 6px 0 10px;
  color: var(--text-3);

  & + & {
    border-top: 1px solid var(--line);
  }
}

.link-label {
  width: 140px;
  padding: 3px 6px;
  border: 1px solid transparent;
  border-radius: 5px;
  background: none;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  color: var(--text);

  &:hover,
  &:focus {
    border-color: var(--line-strong);
    outline: none;
  }
}

.link-url {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
}

.link-remove {
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border: 0;
  border-radius: 6px;
  background: none;
  color: var(--text-3);
  cursor: pointer;

  &:hover {
    background: var(--hover-bg);
    color: var(--text);
  }
}

.link-add {
  display: flex;
  gap: 8px;
}

.link-add-label {
  width: 200px;
  flex-shrink: 0;
}
</style>
